import { useRef, useEffect } from "react";

// formik components
import { Formik, Form } from "formik";

// toast notifications
import { toast, Id } from "react-toastify";

// Material UI components
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Page components
import CountryYearForm from "./sections/countryYearForm";
import AgeGroupsForm from "./sections/ageGroupsForm";
import SurvivalLastYearForm from "./sections/survivalLastYearForm";
import IncidenceAgeScalingForm from "./sections/incidenceAgeScalingForm";
import IncidenceRatesForm from "./sections/incidenceRatesForm";
import InsulinForm from "./sections/insulinForm";
import CumulativesSurvForm from "./sections/cumulativesSurvForm";
import CumulativesIncForm from "./sections/cumulativesIncForm";
import CardSkeleton from "examples/Cards/CardSkeleton";
import CardError from "examples/Cards/CardError";

// API queries
import { useCountryLocCodes } from "api/features/country/queries";

// Contexts
import { useFormAnalyticsContext } from "contexts/contextFormAnalytics";
import { useUserContext } from "contexts/contextUser";

import validations from "../../schemas/countrySchema/validations";
import getForm from "../../schemas/countrySchema/form";

function CountryForm(): JSX.Element {
  const {
    countryDefaults,
    appliedOptions,
    setAppliedOptions,
    isLoading,
    isFormLoading,
    isError,
    errorMessage,
    refetch,
  } = useFormAnalyticsContext();

  const { isAuthenticated } = useUserContext();
  const isAuth = Boolean(isAuthenticated);

  const { data: countryLocCodes } = useCountryLocCodes();
  const { formId, formField } = getForm(countryLocCodes);
  const loadingToastRef = useRef<Id | null>(null);
  const formerValuesRef = useRef<any>(null);

  const handleSubmit = (values: any, actions: any) => {
    if (loadingToastRef.current || formerValuesRef.current === values) {
      actions.setSubmitting(false);
      return;
    }

    loadingToastRef.current = toast.loading("Loading country data...");
    setAppliedOptions(values);
    formerValuesRef.current = values;
    actions.setSubmitting(false);
  };

  useEffect(() => {
    const toastId = loadingToastRef.current;
    if (!toastId || isLoading) return;
    if (appliedOptions !== formerValuesRef.current) return;

    toast.update(toastId, {
      render: isError
        ? errorMessage || "Failed to load country data."
        : "Country data submitted successfully!",
      type: isError ? "error" : "success",
      isLoading: false,
      autoClose: 2500,
    });
    loadingToastRef.current = null;
  }, [appliedOptions, isLoading, isError, errorMessage]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={countryDefaults}
      validationSchema={validations}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, isSubmitting }) => {
        const formData = { values, errors, touched, formField };
        return (
          <Form id={formId} autoComplete="off">
            {isFormLoading ? (
              <MDBox display="flex" flexDirection="column" gap={1}>
                <CardSkeleton height="5rem" />
                <CardSkeleton height="6rem" />
                <CardSkeleton height="5rem" />
                <CardSkeleton height="8rem" />
                <CardSkeleton height="5rem" />
                {isAuth && <CardSkeleton height="6rem" />}
              </MDBox>
            ) : isError ? (
              <CardError height="16rem" message={errorMessage} onRetry={refetch} />
            ) : (
              <MDBox>
                <MDBox mb={2.5}>
                  <MDButton
                    disabled={isSubmitting || Object.keys(errors).length > 0}
                    type="submit"
                    variant="gradient"
                    color="dark"
                  >
                    Submit
                  </MDButton>
                </MDBox>
                <CountryYearForm formData={formData} />

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <MDTypography variant="h6">Age Groups Settings</MDTypography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <AgeGroupsForm formData={formData} />
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <MDTypography variant="h6">Survival Settings</MDTypography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <SurvivalLastYearForm formData={formData} />
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <MDTypography variant="h6">Incidence Settings</MDTypography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <IncidenceAgeScalingForm formData={formData} />
                    <IncidenceRatesForm formData={formData} />
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <MDTypography variant="h6">Insulin Settings</MDTypography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <InsulinForm formData={formData} />
                  </AccordionDetails>
                </Accordion>

                {isAuth && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <MDTypography variant="h6">Cumulative outputs settings</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <CumulativesSurvForm formData={formData} />
                      <CumulativesIncForm formData={formData} />
                    </AccordionDetails>
                  </Accordion>
                )}
              </MDBox>
            )}
          </Form>
        );
      }}
    </Formik>
  );
}

export default CountryForm;
